import React, { useState, useEffect } from 'react';
import { apiCall } from '../config';
import './AmazonInventory.css';

const AmazonInventory = () => {
  const [inventoryData, setInventoryData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [saveMessage, setSaveMessage] = useState(null);

  // Fetch inventory data
  const fetchInventoryData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await apiCall('/api/amazon-inventory/fetch-data', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({})
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Inventory data response received');
        
        if (data.success) {
          console.log('Frontend received inventory data for all SKUs:', data.inventory_data);
          setInventoryData(data.inventory_data || []);
          setLastUpdated(new Date().toLocaleString());
        } else {
          setError(data.error || 'Failed to fetch inventory data');
        }
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch inventory data');
      }
    } catch (error) {
      console.error('Error fetching inventory data:', error);
      setError('Error fetching inventory data. Please check the console for details.');
    } finally {
      setIsLoading(false);
    }
  };

  // Save inventory data to database
  const saveInventoryData = async () => {
    if (inventoryData.length === 0) {
      setError('No inventory data to save. Please fetch data first.');
      return;
    }

    setIsSaving(true);
    setError(null);
    setSaveMessage(null);
    
    try {
      const response = await apiCall('/api/amazon-inventory/save-data', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          inventory_data: inventoryData
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setSaveMessage(`Successfully saved ${data.saved_records} inventory records to database.`);
          if (data.errors && data.errors.length > 0) {
            console.warn('Some errors occurred during save:', data.errors);
          }
        } else {
          setError(data.error || 'Failed to save inventory data');
        }
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to save inventory data');
      }
    } catch (error) {
      console.error('Error saving inventory data:', error);
      setError('Error saving inventory data. Please check the console for details.');
    } finally {
      setIsSaving(false);
    }
  };



  return (
    <div className="amazon-inventory">
      <div className="page-header">
        <div className="header-content">
          <div className="header-title">
            <h1>Amazon Inventory Manager</h1>
          </div>
          <div className="header-tips">
            <ul>
              <li>Fetch current inventory levels from Amazon API</li>
              <li>View AWD and FBA inventory separately</li>
              <li>Manually edit AWD inventory and save to database</li>
            </ul>
          </div>
        </div>
        <div className="header-separator"></div>
      </div>

      <div className="page-content">
        <div className="inventory-controls">
        <div className="button-group">
          <button 
            onClick={fetchInventoryData}
            disabled={isLoading}
            className="fetch-btn"
          >
            {isLoading ? 'Fetching...' : 'Fetch All Amazon Inventory'}
          </button>
          
          <button 
            onClick={saveInventoryData}
            disabled={isSaving || inventoryData.length === 0}
            className="save-btn"
          >
            {isSaving ? 'Saving...' : 'Save to Database'}
          </button>
        </div>
        
        {lastUpdated && (
          <div className="last-updated">
            Last updated: {lastUpdated}
          </div>
        )}
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {saveMessage && (
        <div className="success-message">
          {saveMessage}
        </div>
      )}

      {inventoryData.length > 0 && (
        <div className="inventory-table-container">
          <h3>Current Inventory Levels</h3>
          {inventoryData.length > 0 && (
            <div className="data-notice">
              <p><strong>Amazon Inventory Data</strong> - Showing current inventory levels from Amazon's API.</p>
            </div>
          )}
          <div className="table-wrapper">
            <table className="inventory-table">
              <thead>
                <tr>
                  <th>ASIN</th>
                  <th>Product Name</th>
                  <th style={{backgroundColor: '#e6f3ff'}}>AWD In Transit</th>
                  <th style={{backgroundColor: '#ffe6e6'}}>AWD On Hand</th>
                  <th style={{backgroundColor: '#e6ffe6'}}>FBA Total</th>
                  <th style={{backgroundColor: '#fff2e6'}}>Grand Total</th>
                </tr>
              </thead>
              <tbody>
                {inventoryData.map(item => (
                  <tr key={item.asin}>
                    <td className="asin-cell">{item.asin}</td>
                    <td className="name-cell">{item.product_name || 'N/A'}</td>
                    <td className="quantity-cell" style={{backgroundColor: '#e6f3ff'}}>
                      {item.awd_data?.in_transit || 0}
                    </td>
                    <td className="quantity-cell" style={{backgroundColor: '#ffe6e6'}}>
                      {item.awd_data?.on_hand || 0}
                    </td>
                    <td className="quantity-cell" style={{backgroundColor: '#e6ffe6'}}>
                      {item.fba_data?.total_quantity || 0}
                    </td>
                    <td className="quantity-cell" style={{backgroundColor: '#fff2e6'}}>
                      {(item.awd_data?.in_transit || 0) + (item.awd_data?.on_hand || 0) + (item.fba_data?.total_quantity || 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{fontWeight: 'bold', backgroundColor: '#f5f5f5'}}>
                  <td colSpan="2">TOTALS</td>
                  <td style={{backgroundColor: '#e6f3ff'}}>
                    {inventoryData.reduce((sum, item) => sum + (item.awd_data?.in_transit || 0), 0)}
                  </td>
                  <td style={{backgroundColor: '#ffe6e6'}}>
                    {inventoryData.reduce((sum, item) => sum + (item.awd_data?.on_hand || 0), 0)}
                  </td>
                  <td style={{backgroundColor: '#e6ffe6'}}>
                    {inventoryData.reduce((sum, item) => sum + (item.fba_data?.total_quantity || 0), 0)}
                  </td>
                  <td style={{backgroundColor: '#fff2e6'}}>
                    {inventoryData.reduce((sum, item) => 
                      sum + (item.awd_data?.in_transit || 0) + (item.awd_data?.on_hand || 0) + (item.fba_data?.total_quantity || 0), 0
                    )}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {!isLoading && inventoryData.length === 0 && !error && (
        <div className="no-data">
          <p>No inventory data available. Click "Fetch Current Inventory" to load data.</p>
        </div>
      )}
      </div>
    </div>
  );
};

export default AmazonInventory; 